/**
 * Сервис для работы с мессенджерами и интеграциями
 */

import api from '../relevanter/services/api';
import { useAuthStore } from '../store/authStore';

const getTenantId = () => useAuthStore.getState().user?.companyId;
const getTenantHeaders = () => {
	const tenantId = getTenantId();
	return tenantId ? { 'x-tenant-id': String(tenantId) } : {};
};
const getTenantParams = () => {
	const tenantId = getTenantId();
	return tenantId ? { tenantId } : {};
};

// ============= Types =============

export interface MessengerAccount {
	id: string | number;
	messengerType: 'TELEGRAM' | 'WHATSAPP' | 'MAX';
	phoneNumber?: string;
	accountIdentifier?: string;
	accountName?: string;
	displayName?: string;
	credentials?: string;
	authType?: string;
	authStatus?: 'PENDING' | 'AUTHENTICATED' | 'ACTIVE' | 'ERROR' | 'EXPIRED' | 'LOGGED_OUT';
	isActive?: boolean;
	clientActive?: boolean;
	createdAt?: string;
	updatedAt?: string;
}

export interface Integration {
	type: string;
	status: 'CONNECTED' | 'NOT_CONNECTED';
	metadata?: Record<string, any>;
	/** TASKNEIROKLYUCH-429: основной календарь тенанта */
	isPrimary?: boolean;
}

export interface CalendarSettings {
	clientId: string;
	clientSecret: string;
	redirectUri: string;
}

export interface WhatsAppSettings {
	configured: boolean;
	apiKeyMasked?: string;
	apiKey?: string;
	phoneNumber?: string;
}

// ============= Messenger Accounts =============

/**
 * Получить все аккаунты мессенджеров
 */
export const getMessengerAccounts = async (): Promise<MessengerAccount[]> => {
	const response = await api.get('/messengers', {
		headers: getTenantHeaders(),
		params: { active: true }
	});
	const accountsList = response.data.content || response.data.accounts || response.data || [];
	return Array.isArray(accountsList)
		? accountsList.filter((account) => account?.isActive !== false)
		: [];
};

/**
 * Получить аккаунты Telegram
 */
export const getTelegramAccounts = async (): Promise<MessengerAccount[]> => {
	try {
		const response = await api.get('/messengers/by-type/telegram', {
			headers: getTenantHeaders()
		});
		return response.data.content || response.data || [];
	} catch (error) {
		console.error('Error fetching Telegram accounts:', error);
		return [];
	}
};

/**
 * Создать аккаунт мессенджера
 */
export const createMessengerAccount = async (data: {
	messengerType: 'TELEGRAM' | 'WHATSAPP' | 'MAX';
	phoneNumber?: string;
	accountIdentifier: string;
	accountName: string;
	credentials: string;
	authType: string;
	authStatus: string;
}): Promise<MessengerAccount> => {
	const response = await api.post('/messengers', data, {
		headers: getTenantHeaders()
	});
	return response.data;
};

/**
 * Удалить аккаунт мессенджера
 */
export const deleteMessengerAccount = async (accountId: string | number): Promise<void> => {
	await api.delete(`/messengers/${accountId}`, {
		headers: getTenantHeaders()
	});
};

// ============= Telegram Authentication =============

export interface TelegramAuthResponse {
	status: 'WAITING_CODE' | 'WAITING_FOR_CODE' | 'WAITING_2FA' | 'WAITING_FOR_2FA' | 'AUTHENTICATED' | 'SUCCESS';
	sessionId?: string;
	message?: string;
}

/**
 * Начать аутентификацию Telegram
 */
export const startTelegramAuth = async (
	phoneNumber: string,
	accountName: string,
	options?: { apiId?: string | number; apiHash?: string; accountId?: string | number }
): Promise<TelegramAuthResponse> => {
	const apiId = options?.apiId !== undefined ? Number(options.apiId) : undefined;
	const response = await api.post('/messengers/telegram/auth/start', {
		phoneNumber,
		accountName,
		...(options?.accountId ? { accountId: String(options.accountId) } : {}),
		...(apiId !== undefined && Number.isFinite(apiId) && apiId > 0 ? { apiId } : {}),
		...(options?.apiHash ? { apiHash: options.apiHash } : {})
	}, {
		headers: getTenantHeaders()
	});
	return response.data;
};

/**
 * Проверить код из Telegram
 */
export const verifyTelegramCode = async (sessionId: string, code: string): Promise<TelegramAuthResponse> => {
	const response = await api.post('/messengers/telegram/auth/verify-code', { sessionId, code }, {
		headers: getTenantHeaders()
	});
	return response.data;
};

/**
 * Проверить пароль 2FA
 */
export const verifyTelegram2FA = async (sessionId: string, password: string): Promise<TelegramAuthResponse> => {
	const response = await api.post('/messengers/telegram/auth/verify-2fa', { sessionId, password }, {
		headers: getTenantHeaders()
	});
	return response.data;
};

// ============= Max Authentication (QR flow) =============

export interface MaxAuthStartResponse {
	sessionId: string;
	qrLink: string;
	expiresAt: number;
}

export interface MaxAuthStatusResponse {
	status: 'pending' | 'authenticated' | 'expired' | 'not_found' | 'error' | 'password_required';
	message?: string;
	hint?: string;
	email?: string;
}

export interface MaxAuthCompleteResponse {
	accountId: string;
	accountName: string;
}

export interface MaxAuthPasswordResponse {
	status: 'authenticated' | 'error';
	message?: string;
}

/**
 * Начать QR-авторизацию Max.
 * Возвращает ссылку, из которой фронт рисует QR-код, и sessionId для
 * последующих вызовов pollMaxAuthStatus / completeMaxAuth.
 */
export const startMaxAuth = async (): Promise<MaxAuthStartResponse> => {
	const response = await api.post('/messengers/max/auth/start', {}, {
		headers: getTenantHeaders()
	});
	return response.data;
};

/**
 * Опросить статус сканирования QR.
 * Frontend должен вызывать это каждые 2-3 секунды, пока не получит
 * status === 'authenticated' или терминальное состояние (expired/error).
 */
export const pollMaxAuthStatus = async (sessionId: string): Promise<MaxAuthStatusResponse> => {
	const response = await api.get(`/messengers/max/auth/status/${sessionId}`, {
		headers: getTenantHeaders()
	});
	return response.data;
};

/**
 * Отправить облачный пароль для 2FA после сканирования QR.
 * Вызывается когда pollMaxAuthStatus вернул 'password_required'.
 */
export const submitMaxAuthPassword = async (
	sessionId: string,
	password: string
): Promise<MaxAuthPasswordResponse> => {
	const response = await api.post('/messengers/max/auth/password', { sessionId, password }, {
		headers: getTenantHeaders()
	});
	return response.data;
};

/**
 * Завершить авторизацию после сканирования QR.
 * Вызывается после того как pollMaxAuthStatus вернул 'authenticated'.
 * Создаёт запись в БД и активирует постоянный клиент.
 */
export const completeMaxAuth = async (
	sessionId: string,
	accountName: string
): Promise<MaxAuthCompleteResponse> => {
	const response = await api.post('/messengers/max/auth/complete', { sessionId, accountName }, {
		headers: getTenantHeaders()
	});
	return response.data;
};

// ============= WhatsApp Dialog360 =============

/**
 * Получить глобальные настройки WhatsApp (Dialog360)
 */
export const getWhatsAppSettings = async (): Promise<WhatsAppSettings> => {
	try {
		const response = await api.get('/messengers/settings/whatsapp', {
			headers: getTenantHeaders()
		});
		return {
			configured: response.data.configured,
			apiKeyMasked: response.data.apiKeyMasked || '',
			phoneNumber: response.data.phoneNumber || '',
			apiKey: ''
		};
	} catch (error) {
		console.error('Error fetching WhatsApp settings:', error);
		return {
			configured: false,
			apiKeyMasked: '',
			phoneNumber: '',
			apiKey: ''
		};
	}
};

/**
 * Сохранить настройки WhatsApp (Dialog360)
 */
export const saveWhatsAppSettings = async (data: { apiKey?: string; phoneNumber?: string }): Promise<void> => {
	await api.post('/messengers/settings/whatsapp', data, {
		headers: getTenantHeaders()
	});
};

/**
 * Удалить настройки WhatsApp (Dialog360)
 */
export const deleteWhatsAppSettings = async (): Promise<void> => {
	await api.delete('/messengers/settings/whatsapp', {
		headers: getTenantHeaders()
	});
};

// ============= Integrations =============

/**
 * Получить список интеграций
 */
export const getIntegrations = async (): Promise<Integration[]> => {
	const response = await api.get('/ai-recruiter/integrations', { params: getTenantParams() });
	return response.data;
};

/**
 * Получить настройки Google Calendar
 */
export const getCalendarSettings = async (): Promise<CalendarSettings> => {
	const response = await api.get('/ai-recruiter/integrations/settings/GOOGLE_CALENDAR', { params: getTenantParams() });
	return response.data;
};

/**
 * Сохранить настройки Google Calendar
 */
export const saveCalendarSettings = async (settings: CalendarSettings): Promise<void> => {
	await api.post('/ai-recruiter/integrations/settings/GOOGLE_CALENDAR', settings, { params: getTenantParams() });
};

/**
 * Очистить настройки Google Calendar (возврат к пустым значениям по умолчанию).
 */
export const clearCalendarSettings = async (): Promise<void> => {
	await api.delete('/ai-recruiter/integrations/settings/GOOGLE_CALENDAR', { params: getTenantParams() });
};

/**
 * Получить URL для авторизации Google Calendar
 */
export const getGoogleCalendarAuthUrl = async (): Promise<{ authUrl: string }> => {
	const response = await api.get('/ai-recruiter/integrations/google-calendar/auth-url', { params: getTenantParams() });
	return response.data;
};

/**
 * Универсальный OAuth URL для любого календарного провайдера (TASKNEIROKLYUCH-429).
 * type: "GOOGLE_CALENDAR" | "YANDEX_CALENDAR" | ...
 */
export const getCalendarAuthUrl = async (type: string): Promise<{ authUrl: string }> => {
	const response = await api.get(`/ai-recruiter/integrations/${type}/auth-url`, { params: getTenantParams() });
	return response.data;
};

/**
 * Подключение Яндекс.Календаря через login + пароль приложения (TASKNEIROKLYUCH-429).
 * У Яндекса нет OAuth для Календаря — используется обычная Basic Auth поверх CalDAV.
 * Пароль приложения создаётся в passport.yandex.ru → "Пароли приложений".
 */
export const connectYandexCalendar = async (login: string, appPassword: string): Promise<void> => {
	await api.post(
		'/ai-recruiter/integrations/YANDEX_CALENDAR/connect',
		{ login, appPassword },
		{ params: getTenantParams() }
	);
};

/**
 * Сделать указанный календарь основным (TASKNEIROKLYUCH-429).
 * Когда у тенанта подключено несколько (Google + Yandex), события создаются и
 * занятость считается именно по primary.
 */
export const setPrimaryCalendar = async (type: string): Promise<void> => {
	await api.post(`/ai-recruiter/integrations/${type}/primary`, {}, { params: getTenantParams() });
};

/**
 * Отключить интеграцию
 */
export const disconnectIntegration = async (integrationType: string): Promise<void> => {
	await api.delete(`/ai-recruiter/integrations/${integrationType}`, { params: getTenantParams() });
};

// ============= Export =============

const messengerService = {
	// Messenger Accounts
	getMessengerAccounts,
	getTelegramAccounts,
	createMessengerAccount,
	deleteMessengerAccount,

	// Telegram Auth
	startTelegramAuth,
	verifyTelegramCode,
	verifyTelegram2FA,

	// Max Auth (QR flow)
	startMaxAuth,
	pollMaxAuthStatus,
	completeMaxAuth,
	submitMaxAuthPassword,

	// WhatsApp Dialog360
	getWhatsAppSettings,
	saveWhatsAppSettings,
	deleteWhatsAppSettings,

	// Integrations
	getIntegrations,
	getCalendarSettings,
	saveCalendarSettings,
	clearCalendarSettings,
	getGoogleCalendarAuthUrl,
	getCalendarAuthUrl,
	connectYandexCalendar,
	setPrimaryCalendar,
	disconnectIntegration
};

export default messengerService;
